import { FC, useState, useRef, useEffect } from 'react';
import { css } from '@emotion/react';
import { colors, spacing } from '../../../styles';
import { TimeRange } from '../../../types';
import Chart from '../../SymbolPage/Chart';
import ColoredPercentChange from '../../ColoredPercentChange';
import { ToolRendererProps } from './types';

// Time range display labels (using "past" instead of "last")
const timeRangeDisplay: Record<TimeRange, string> = {
  [TimeRange.days1]: 'past day',
  [TimeRange.weeks1]: 'past week',
  [TimeRange.months1]: 'past month',
  [TimeRange.months3]: 'past 3 mo',
  [TimeRange.months6]: 'past 6 mo',
  [TimeRange.ytd]: 'YTD',
  [TimeRange.years1]: 'past year',
  [TimeRange.years5]: 'past 5 yrs',
};

// Financial chart type labels
const chartTypeLabels: Record<string, string> = {
  revenue: 'Revenue',
  revenue_growth: 'Revenue Growth',
  operating_margin: 'Operating Margin',
  net_earnings: 'Net Earnings',
  net_earnings_growth: 'Net Earnings Growth',
  free_cash_flow: 'Free Cash Flow',
  free_cash_flow_growth: 'Free Cash Flow Growth',
};

interface ChartElement {
  price: number;
  benchmark?: number;
  date: Date;
  minute: string;
  changeRatio?: number;
  articleHeadline?: string;
}

interface FinancialDataPoint {
  fiscal_date: string;
  value: number;
  label: string;
}

type FinancialPeriod = 'quarterly' | 'annual';

// Helper to format large numbers
const formatValue = (value: number, unit: string): string => {
  if (unit === '%') {
    return `${value.toFixed(1)}%`;
  }
  const absValue = Math.abs(value);
  if (absValue >= 1e12) return `$${(value / 1e12).toFixed(1)}T`;
  if (absValue >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
  if (absValue >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  if (absValue >= 1e3) return `$${(value / 1e3).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
};

// Calculate rolling 4-quarter average for quarterly data
const calculateRollingAverage = (data: FinancialDataPoint[], windowSize = 4): number[] => {
  const averages: number[] = [];
  for (let i = 0; i < data.length; i++) {
    if (i < windowSize - 1) {
      // Not enough data points yet for a full window
      averages.push(NaN);
    } else {
      const windowData = data.slice(i - windowSize + 1, i + 1);
      const sum = windowData.reduce((acc, d) => acc + d.value, 0);
      averages.push(sum / windowSize);
    }
  }
  return averages;
};

// Financial Chart Component - Vertical bars with rolling average line
const FinancialChart: FC<{
  data: FinancialDataPoint[];
  unit: string;
  chartType: string;
  isQuarterly: boolean;
}> = ({ data, unit, chartType, isQuarterly }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(0);

  // Measure chart width for proper SVG rendering
  useEffect(() => {
    const updateWidth = () => {
      if (chartRef.current) {
        setChartWidth(chartRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  if (!data || data.length === 0) {
    return (
      <div
        css={css`
          font-size: 14px;
          color: ${colors.silver};
          padding: ${spacing.deci};
        `}
      >
        No data available for this period
      </div>
    );
  }

  const isGrowthChart = chartType.includes('growth');
  const chartHeight = 180;
  const barGap = 8;
  const padding = 8; // spacing.centi equivalent

  // Calculate rolling average for quarterly data (only for non-growth charts)
  const rollingAverages = isQuarterly && !isGrowthChart ? calculateRollingAverage(data) : [];

  // Find max value considering both bars and rolling average line
  const allValues = [...data.map((d) => Math.abs(d.value))];
  if (rollingAverages.length > 0) {
    allValues.push(...rollingAverages.filter((v) => !isNaN(v)));
  }
  const maxValue = Math.max(...allValues);
  const minValue = Math.min(...data.map((d) => d.value), 0);

  // For growth charts, we need to handle negative values with center baseline
  const hasNegative = minValue < 0;

  // Calculate bar height percentage
  const getBarHeight = (value: number): number => {
    if (maxValue === 0) return 0;
    if (hasNegative) {
      const range = Math.max(maxValue, Math.abs(minValue)) * 2;
      return (Math.abs(value) / range) * 100;
    }
    return (value / maxValue) * 100;
  };

  // Calculate Y position for rolling average line (in pixels)
  const getYPositionPx = (value: number): number => {
    if (maxValue === 0 || isNaN(value)) return chartHeight;
    if (hasNegative) {
      const range = Math.max(maxValue, Math.abs(minValue));
      const centerY = chartHeight / 2;
      return centerY - (value / range) * (chartHeight / 2);
    }
    return chartHeight - (value / maxValue) * chartHeight;
  };

  // Calculate X position for a bar center (in pixels)
  const getXPositionPx = (index: number): number => {
    if (chartWidth === 0 || data.length === 0) return 0;
    const usableWidth = chartWidth - padding * 2;
    const totalGaps = (data.length - 1) * barGap;
    const barWidth = (usableWidth - totalGaps) / data.length;
    return padding + index * (barWidth + barGap) + barWidth / 2;
  };

  // Build SVG path for rolling average line using pixel coordinates
  const buildLinePathPx = (): string => {
    if (rollingAverages.length === 0 || chartWidth === 0) return '';

    const validPoints: { x: number; y: number }[] = [];

    rollingAverages.forEach((avg, i) => {
      if (!isNaN(avg)) {
        const x = getXPositionPx(i);
        const y = getYPositionPx(avg);
        validPoints.push({ x, y });
      }
    });

    if (validPoints.length < 2) return '';

    return validPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  };

  const linePath = buildLinePathPx();
  const latestAverage = rollingAverages.filter((v) => !isNaN(v)).pop();

  // Handle touch/mouse interaction
  const handleInteraction = (index: number) => {
    setHoveredIndex(index);
  };

  const handleInteractionEnd = () => {
    setHoveredIndex(null);
  };

  return (
    <div
      css={css`
        padding: ${spacing.centi} 0;
      `}
    >
      {/* Chart area */}
      <div
        ref={chartRef}
        css={css`
          position: relative;
          height: ${chartHeight}px;
          display: flex;
          align-items: flex-end;
          gap: ${barGap}px;
          padding: 0 ${spacing.centi};
          touch-action: none;
        `}
        onMouseLeave={handleInteractionEnd}
        onTouchEnd={handleInteractionEnd}
      >
        {/* Center line for growth charts */}
        {hasNegative && (
          <div
            css={css`
              position: absolute;
              left: 0;
              right: 0;
              top: 50%;
              height: 1px;
              background-color: ${colors.cloud};
              pointer-events: none;

              html[data-theme='dark'] & {
                background-color: ${colors.tungsten};
              }
            `}
          />
        )}

        {/* SVG overlay for rolling average line */}
        {linePath && chartWidth > 0 && (
          <svg
            css={css`
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              height: 100%;
              pointer-events: none;
              overflow: visible;
            `}
          >
            <path
              d={linePath}
              fill="none"
              stroke={colors.earth}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {/* Dots at each point */}
            {rollingAverages.map((avg, i) => {
              if (isNaN(avg)) return null;
              const x = getXPositionPx(i);
              const y = getYPositionPx(avg);
              const isHovered = hoveredIndex === i;
              return (
                <circle
                  key={i}
                  cx={x}
                  cy={y}
                  r={isHovered ? 5 : 3}
                  fill={isHovered ? colors.earth : colors.earth}
                  stroke="white"
                  strokeWidth={isHovered ? 2 : 0}
                />
              );
            })}
          </svg>
        )}

        {/* Vertical bars */}
        {data.map((point, index) => {
          const barHeight = getBarHeight(point.value);
          const isPositive = point.value >= 0;
          const isHovered = hoveredIndex === index;
          const hoveredAvg = !isNaN(rollingAverages[index]) ? rollingAverages[index] : null;

          return (
            <div
              key={index}
              css={css`
                flex: 1 1 0;
                min-width: 0;
                display: flex;
                flex-direction: column;
                align-items: center;
                height: 100%;
                position: relative;
                cursor: pointer;
              `}
              onMouseEnter={() => handleInteraction(index)}
              onTouchStart={() => handleInteraction(index)}
            >
              {/* Tooltip above bar */}
              {isHovered && (
                <div
                  css={css`
                    position: absolute;
                    bottom: 100%;
                    left: 50%;
                    transform: translateX(-50%);
                    margin-bottom: ${spacing.milli};
                    padding: 4px 8px;
                    background-color: ${colors.liquorice};
                    border-radius: 4px;
                    white-space: nowrap;
                    z-index: 10;
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 2px;

                    html[data-theme='dark'] & {
                      background-color: var(--card-background);
                    }

                    &::after {
                      content: '';
                      position: absolute;
                      top: 100%;
                      left: 50%;
                      transform: translateX(-50%);
                      border: 4px solid transparent;
                      border-top-color: ${colors.liquorice};

                      html[data-theme='dark'] & {
                        border-top-color: var(--card-background);
                      }
                    }
                  `}
                >
                  <span
                    css={css`
                      font-size: 12px;
                      font-weight: 600;
                      color: ${colors.sugar};
                    `}
                  >
                    {point.label}: {formatValue(point.value, unit)}
                  </span>
                  {hoveredAvg !== null && (
                    <span
                      css={css`
                        font-size: 10px;
                        color: ${colors.earth};
                      `}
                    >
                      Avg: {formatValue(hoveredAvg, unit)}
                    </span>
                  )}
                </div>
              )}

              {/* Bar container */}
              <div
                css={css`
                  width: 100%;
                  height: 100%;
                  display: flex;
                  flex-direction: column;
                  justify-content: ${hasNegative ? 'center' : 'flex-end'};
                  align-items: center;
                `}
              >
                {/* The bar */}
                <div
                  css={css`
                    width: 100%;
                    max-width: 48px;
                    height: ${barHeight}%;
                    min-height: 2px;
                    background-color: ${isGrowthChart
                      ? isPositive
                        ? isHovered
                          ? colors.leaf
                          : colors.leaf
                        : isHovered
                          ? colors.berry
                          : colors.berry
                      : isHovered
                        ? colors.petrolBlue
                        : colors.petrolBlue};
                    border-radius: 4px 4px ${hasNegative && !isPositive ? '4px 4px' : '0 0'};
                    transition: background-color 0.15s ease;
                    ${hasNegative && !isPositive ? 'margin-top: 0;' : ''}
                    ${hasNegative && isPositive ? 'margin-bottom: 0;' : ''}

                    html[data-theme='dark'] & {
                      background-color: ${isGrowthChart
                        ? isPositive
                          ? isHovered
                            ? colors.leaf
                            : colors.leaf
                          : isHovered
                            ? colors.berry
                            : colors.berry
                        : isHovered
                          ? colors.skyBlue
                          : colors.skyBlue};
                    }
                  `}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Labels row - hide when chart is too narrow (less than 40px per bar) */}
      {chartWidth / data.length >= 40 && (
        <div
          css={css`
            display: flex;
            gap: ${barGap}px;
            padding: ${spacing.milli} ${spacing.centi} 0;
          `}
        >
          {data.map((point, index) => {
            const isPositive = point.value >= 0;
            const isHovered = hoveredIndex === index;
            return (
              <div
                key={index}
                css={css`
                  flex: 1 1 0;
                  min-width: 0;
                  text-align: center;
                `}
              >
                {/* Value - hide when there's a hovered item to reduce visual noise */}
                <div
                  css={css`
                    font-size: 11px;
                    font-weight: 600;
                    color: ${isGrowthChart ? (isPositive ? colors.leaf : colors.berry) : colors.liquorice};
                    margin-bottom: ${spacing.micro};
                    opacity: ${hoveredIndex !== null ? (isHovered ? 1 : 0.3) : 1};
                    transition: opacity 0.15s ease;

                    html[data-theme='dark'] & {
                      color: ${isGrowthChart ? (isPositive ? colors.leaf : colors.berry) : colors.sugar};
                    }
                  `}
                >
                  {formatValue(point.value, unit)}
                </div>
                {/* Label */}
                <div
                  css={css`
                    font-size: 10px;
                    color: ${colors.silver};
                    opacity: ${hoveredIndex !== null ? (isHovered ? 1 : 0.5) : 1};
                    transition: opacity 0.15s ease;

                    html[data-theme='dark'] & {
                      color: ${colors.tungsten};
                    }
                  `}
                >
                  {point.label}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Legend for rolling average */}
      {rollingAverages.length > 0 && latestAverage !== undefined && (
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: ${spacing.milli};
            padding: ${spacing.centi} ${spacing.centi} 0;
            font-size: 11px;
            color: ${colors.silver};

            html[data-theme='dark'] & {
              color: ${colors.tungsten};
            }
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 4px;
            `}
          >
            <div
              css={css`
                width: 16px;
                height: 2px;
                background-color: ${colors.earth};
                border-radius: 1px;
              `}
            />
            <span>4Q Avg: {formatValue(latestAverage, unit)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Chart Renderer - Display interactive charts for get_price_charts tool
export const ChartRenderer: FC<ToolRendererProps> = ({ output, isLoading }) => {
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRange>(TimeRange.months3);
  const [selectedPeriod, setSelectedPeriod] = useState<FinancialPeriod>('quarterly');

  if (isLoading) {
    return (
      <div
        css={css`
          font-size: 14px;
          color: ${colors.silver};
        `}
      >
        Loading charts...
      </div>
    );
  }

  // Handle error cases
  if (output.error) {
    return (
      <div
        css={css`
          font-size: 14px;
          color: ${colors.berry};
          padding: ${spacing.centi};
          background-color: rgba(208, 63, 23, 0.05);
          border-radius: 6px;
          border: 1px solid rgba(208, 63, 23, 0.15);
          margin: ${spacing.centi} 0;

          html[data-theme='dark'] & {
            background-color: rgba(208, 63, 23, 0.1);
            border-color: rgba(208, 63, 23, 0.2);
            color: ${colors.berry};
          }
        `}
      >
        <div
          css={css`
            font-weight: 600;
            margin-bottom: ${spacing.milli};
          `}
        >
          Chart Error
        </div>
        <div>{output.error}</div>
      </div>
    );
  }

  const symbol = output.symbol;
  const chartType = output.chart_type || 'price';
  const timeframes = output.timeframes || output.charts;
  const financialData = output.data;
  const metadata = output.metadata;

  // Check if this is a financial chart (has data.quarterly/annual) or price chart (has timeframes)
  const isFinancialChart = financialData && (financialData.quarterly || financialData.annual);

  if (!isFinancialChart && (!timeframes || !symbol)) {
    return (
      <div
        css={css`
          font-size: 14px;
          color: ${colors.silver};
        `}
      >
        No chart data available
      </div>
    );
  }

  // Render financial chart
  if (isFinancialChart) {
    const quarterlyData: FinancialDataPoint[] = financialData.quarterly || [];
    const annualData: FinancialDataPoint[] = financialData.annual || [];
    const unit = metadata?.unit || 'USD';
    const currentData = selectedPeriod === 'quarterly' ? quarterlyData : annualData;

    return (
      <div
        css={css`
          margin: ${spacing.centi} 0;
        `}
      >
        {/* Header with chart type and period selector */}
        <div
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: ${spacing.centi};
          `}
        >
          <div
            css={css`
              font-size: 14px;
              font-weight: 600;
              color: ${colors.liquorice};

              html[data-theme='dark'] & {
                color: ${colors.sugar};
              }
            `}
          >
            {symbol} {chartTypeLabels[chartType] || chartType}
          </div>

          {/* Period selector */}
          <div
            css={css`
              display: flex;
              gap: ${spacing.milli};
            `}
          >
            {(['quarterly', 'annual'] as FinancialPeriod[]).map((period) => {
              const hasData = period === 'quarterly' ? quarterlyData.length > 0 : annualData.length > 0;
              return (
                <button
                  key={period}
                  onClick={() => setSelectedPeriod(period)}
                  disabled={!hasData}
                  css={css`
                    padding: 4px 10px;
                    border: 1px solid ${selectedPeriod === period ? colors.petrolBlue : colors.cloud};
                    background-color: ${selectedPeriod === period ? colors.petrolBlue : 'transparent'};
                    color: ${selectedPeriod === period ? colors.sugar : colors.liquorice};
                    border-radius: 4px;
                    font-size: 12px;
                    cursor: ${hasData ? 'pointer' : 'not-allowed'};
                    opacity: ${hasData ? 1 : 0.5};
                    transition: all 0.2s;

                    &:hover {
                      border-color: ${hasData ? colors.petrolBlue : colors.cloud};
                    }

                    html[data-theme='dark'] & {
                      color: ${selectedPeriod === period ? colors.liquorice : colors.sugar};
                      border-color: ${selectedPeriod === period ? colors.skyBlue : colors.tungsten};
                      background-color: ${selectedPeriod === period ? colors.skyBlue : 'transparent'};

                      &:hover {
                        border-color: ${hasData ? colors.skyBlue : colors.tungsten};
                      }
                    }
                  `}
                >
                  {period === 'quarterly' ? 'Quarterly' : 'Annual'}
                </button>
              );
            })}
          </div>
        </div>

        {/* Financial chart display */}
        <FinancialChart
          data={currentData}
          unit={unit}
          chartType={chartType}
          isQuarterly={selectedPeriod === 'quarterly'}
        />
      </div>
    );
  }

  // Convert timeframes data to the format expected by Chart component
  const convertedCharts: Record<TimeRange, ChartElement[] | undefined> = {
    [TimeRange.days1]: undefined,
    [TimeRange.weeks1]: undefined,
    [TimeRange.months1]: undefined,
    [TimeRange.months3]: undefined,
    [TimeRange.months6]: undefined,
    [TimeRange.ytd]: undefined,
    [TimeRange.years1]: undefined,
    [TimeRange.years5]: undefined,
  };

  Object.entries(timeframes).forEach(([timeframe, rawData]) => {
    // Handle nested .data format (e.g., { "1m": { data: [...] } }) or flat array
    const data = Array.isArray(rawData)
      ? rawData
      : Array.isArray((rawData as any)?.data)
        ? (rawData as any).data
        : null;
    if (Array.isArray(data) && data.length > 0) {
      // Convert the data to ChartElement format - handle both close and price field names
      const convertedData: ChartElement[] = data.map((item: any) => {
        const price =
          typeof item.price === 'number'
            ? item.price
            : typeof item.close === 'number'
              ? item.close
              : parseFloat(item.price || item.close || 0);
        return {
          price,
          date: new Date(item.date || item.datetime),
          minute: item.datetime || item.date,
          changeRatio: item.changeRatio,
          benchmark: item.benchmark,
        };
      });

      // Calculate changeRatio from prices if not provided by backend
      if (convertedData.length > 0 && convertedData[0].changeRatio === undefined) {
        const basePrice = convertedData[0].price;
        if (basePrice > 0) {
          for (const point of convertedData) {
            point.changeRatio = (point.price - basePrice) / basePrice;
          }
        }
      }

      // Map timeframe strings to TimeRange enum
      const mappedTimeframe = timeframe as TimeRange;
      if (Object.values(TimeRange).includes(mappedTimeframe)) {
        convertedCharts[mappedTimeframe] = convertedData;
      }
    }
  });

  // Select default timeframe based on available data
  const availableTimeframes = Object.entries(convertedCharts)
    .filter(([, data]) => data !== undefined)
    .map(([timeframe]) => timeframe as TimeRange);
  const defaultTimeframe = availableTimeframes.includes(TimeRange.months3) ? TimeRange.months3 : availableTimeframes[0];

  // Use selected timeframe only if it has data, otherwise fall back to default
  const currentTimeframe = convertedCharts[selectedTimeRange] ? selectedTimeRange : defaultTimeframe;
  const currentData = convertedCharts[currentTimeframe];
  let changeRatio: number | undefined = undefined;

  if (currentData && currentData.length > 1) {
    const firstPrice = currentData[0].price;
    const lastPrice = currentData[currentData.length - 1].price;
    if (firstPrice && lastPrice) {
      changeRatio = (lastPrice - firstPrice) / firstPrice;
    }
  }

  return (
    <div
      css={css`
        margin: ${spacing.centi} 0;
      `}
    >
      {/* Time range selector and percentage */}
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: ${spacing.centi};
        `}
      >
        {changeRatio !== undefined && (
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: ${spacing.centi};
            `}
          >
            <ColoredPercentChange
              value={changeRatio}
              css={css`
                font-size: 14px;
              `}
            />
            {timeRangeDisplay[currentTimeframe] && (
              <span
                css={css`
                  color: ${colors.silver};
                  font-size: 14px;

                  html[data-theme='dark'] & {
                    color: ${colors.sugar};
                  }
                `}
              >
                {timeRangeDisplay[currentTimeframe]}
              </span>
            )}
          </div>
        )}

        {availableTimeframes.length >= 1 && (
          <div
            css={css`
              display: flex;
              gap: ${spacing.milli};
            `}
          >
            {availableTimeframes.map((timeframe) => (
              <button
                key={timeframe}
                onClick={() => setSelectedTimeRange(timeframe)}
                css={css`
                  padding: 4px 8px;
                  border: 1px solid ${currentTimeframe === timeframe ? colors.petrolBlue : colors.cloud};
                  background-color: ${currentTimeframe === timeframe ? colors.petrolBlue : 'transparent'};
                  color: ${currentTimeframe === timeframe ? colors.sugar : colors.liquorice};
                  border-radius: 4px;
                  font-size: 12px;
                  cursor: pointer;
                  transition: all 0.2s;

                  &:hover {
                    border-color: ${colors.petrolBlue};
                  }

                  html[data-theme='dark'] & {
                    color: ${currentTimeframe === timeframe ? colors.liquorice : colors.sugar};
                    border-color: ${currentTimeframe === timeframe ? colors.skyBlue : colors.tungsten};
                    background-color: ${currentTimeframe === timeframe ? colors.skyBlue : 'transparent'};

                    &:hover {
                      border-color: ${colors.skyBlue};
                    }
                  }
                `}
              >
                {timeframe.toUpperCase()}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Chart display */}
      <div
        css={css`
          height: 300px;
          position: relative;
        `}
      >
        <Chart
          charts={convertedCharts}
          isLoading={false}
          isError={false}
          onRetry={() => {
            // No retry functionality needed for chart renderer
          }}
          timeRange={currentTimeframe}
          strokeColor={colors.petrolBlue}
          selectedNewsElement={undefined}
          onSelectNews={() => {
            // No news selection functionality in chart renderer
          }}
          scannedPrice={undefined}
          onScanPrice={() => {
            // No price scanning functionality in chart renderer
          }}
          ticker={symbol}
          showWiim={false}
        />
      </div>
    </div>
  );
};
